// src/hoc/RootLayout.tsx
import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import {
  closeCartModal,
  closeComplexOrderCartModal,
  closeServiceModal,
} from "../store/slices/commonModalSlice";
import { store } from "../store/store";
import { useRootLayout } from "./useRootLayout";
import { NoNetworkView } from "./components/noNetworkView/NoNetworkView";
import { EmptyCatalogView } from "./components/emptyCatalogView/EmptyCatalogView";
import { ErrorScreen } from "./components/errorScreen/ErrorScreen";
import { LoadingScreen } from "./components/loadingScreen/LoadingScreen";
import BottomModal from "../common/components/bottomModal/BottomModal";
import CartModalContent from "../common/components/cartModal/CartModalContent";
import ComplexCartModal from "../common/components/complexCartModal/ComplexCartModal";
import { ServiceModalContent } from "../common/components/serviceModalContent/ServiceModalContent";
import { useTheme } from "../common/hooks/useTheme";
import styles from "./RootLayout.module.scss";
import { Footer } from "../common/components/footer/Footer";
import { useScrollToTop } from "../common/hooks/useScrollToTop";
import { ScrollTopButton } from "../common/components/scrollTopButton/ScrollTopButton";
import { useNetworkMonitor } from "../common/hooks/useNetworkMonitor";
import { ForceStoreSelector } from "../common/components/forceStoreSelector/ForceStoreSelector";
import { StoreSelectorButton } from "../common/components/storeSelectorButton/StoreSelectorButton";
import { selectCurrentCluster } from "../store/slices/currentClusterSlice";
import { selectStoreList } from "../store/slices/storeListSlice";
import { TechnicalWorkScreen } from "./components/technicalWorkScreen/TechnicalWorkScreen";
import { AppPromotion } from "../common/components/appPromotion/AppPromotion";
import Navigation from "../common/components/navigation";
import { ServiceModeIndicator } from "../common/components/serviceModeIndicator/ServiceModeIndicator";
import useMetrics from "../common/hooks/useMetrics";
import { CallButton } from "../common/components/callButton/CallButton";
import { CookieBanner } from "../common/components/cookieBanner/CookieBanner";
import { useScreenTracking } from "../common/hooks/useScreenTracking";

interface RootLayoutProps {
  children: ReactNode;
}

export default function RootLayout({ children }: RootLayoutProps) {
  useScreenTracking();
  useMetrics();
  const { hasDataError, hasNetwork, isDataLoaded, reloadData } =
    useRootLayout();
  useNetworkMonitor(isDataLoaded);
  const { cartModal, complexOrderCartModal, serviceModal } = useSelector(
    (state: RootState) => state.commonModal,
  );
  const hasProducts = useSelector(
    (state: RootState) => state.saleItemList.data.ids.length > 0,
  );

  const currentCluster = useSelector(selectCurrentCluster);
  const storeList = useSelector(selectStoreList);
  const activeStores = storeList.filter(
    (s) => s.isActive && !s.temporarilyDisabled,
  );
  const hasNoActiveStores = activeStores.length === 0;
  const clusterInactive =
    !currentCluster?.isActive || currentCluster?.temporarilyDisabled;

  useTheme();
  useScrollToTop();

  if (!isDataLoaded) {
    return <LoadingScreen />;
  }

  // 🔥 Защита от гонки: если кластер не загружен — ждём
  if (!currentCluster) {
    return <LoadingScreen />;
  }

  if (clusterInactive || hasNoActiveStores) {
    return <TechnicalWorkScreen onRetry={reloadData} />;
  }

  if (!hasProducts) {
    if (hasNetwork === false) {
      return <NoNetworkView onRetry={reloadData} hasCache={false} />;
    }
    return <EmptyCatalogView onRetry={reloadData} />;
  }

  if (hasDataError) {
    return <ErrorScreen error={new Error("Не удалось загрузить данные")} />;
  }

  return (
    <div className={styles.root}>
      <CookieBanner />
      {isDataLoaded && <AppPromotion />}
      <ForceStoreSelector />
      <StoreSelectorButton />
      <ServiceModeIndicator />
      {children}
      <CallButton />
      <ScrollTopButton />
      <Navigation />
      <Footer />
      <BottomModal
        isVisible={cartModal.isOpen}
        onClose={() => store.dispatch(closeCartModal())}
      >
        <CartModalContent onClose={() => store.dispatch(closeCartModal())} />
      </BottomModal>
      <BottomModal
        isVisible={complexOrderCartModal.isOpen}
        onClose={() => store.dispatch(closeComplexOrderCartModal())}
      >
        <ComplexCartModal
          onClose={() => store.dispatch(closeComplexOrderCartModal())}
        />
      </BottomModal>
      <BottomModal
        isVisible={serviceModal.isOpen}
        onClose={() => store.dispatch(closeServiceModal())}
      >
        <ServiceModalContent
          onClose={() => store.dispatch(closeServiceModal())}
        />
      </BottomModal>
    </div>
  );
}
